import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { requestsAPI, spaceTypesAPI, storesAPI, artworksAPI, modificationsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Clock, CheckCircle2, AlertCircle, Building2, Package, MessageSquare, ThumbsUp, ThumbsDown, Image, Download, Eye, Upload, Edit, History, Calendar, ArrowRight, XCircle, FileSpreadsheet } from 'lucide-react';

const statusColors = {
  DRAFT: 'bg-slate-100 text-slate-700',
  SUBMITTED: 'bg-blue-100 text-blue-700',
  REVIEW: 'bg-yellow-100 text-yellow-700',
  APPROVED: 'bg-emerald-100 text-emerald-700',
  REJECTED: 'bg-red-100 text-red-700',
  CONFIRMED: 'bg-green-100 text-green-700',
  MODIFIED: 'bg-orange-100 text-orange-700',
  MODIFICATION_ACCEPTED: 'bg-emerald-100 text-emerald-700',
  MODIFICATION_REJECTED: 'bg-red-100 text-red-700',
};

const statusLabels = {
  DRAFT: 'Borrador',
  SUBMITTED: 'Enviada',
  REVIEW: 'En Revisión',
  APPROVED: 'Aprobada',
  REJECTED: 'Rechazada',
  CONFIRMED: 'Confirmada',
  MODIFIED: 'Modificada (Pendiente)',
  MODIFICATION_ACCEPTED: 'Modificación Aceptada',
  MODIFICATION_REJECTED: 'Modificación Rechazada',
};

export default function RequestDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isExecutive, isAdmin, isProvider } = useAuth();
  const [request, setRequest] = useState(null);
  const [spaceTypes, setSpaceTypes] = useState({});
  const [stores, setStores] = useState({});
  const [storesList, setStoresList] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [actionComment, setActionComment] = useState('');
  const [showApproveReject, setShowApproveReject] = useState(false);
  
  // Modification states
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [modifications, setModifications] = useState([]);
  const [pendingModification, setPendingModification] = useState(null);
  const [allocationChanges, setAllocationChanges] = useState([]);
  const [modificationNotes, setModificationNotes] = useState('');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadRequestDetail();
  }, [id]);

  const loadRequestDetail = async () => {
    try {
      const [requestRes, spacesRes, storesRes, artworksRes] = await Promise.all([
        requestsAPI.getById(id),
        spaceTypesAPI.getAll(),
        storesAPI.getAll(),
        artworksAPI.getByRequest(id).catch(() => ({ data: [] }))
      ]);
      
      setRequest(requestRes.data);
      setArtworks(artworksRes.data || []);
      setStoresList(storesRes.data);
      
      const spacesMap = {};
      spacesRes.data.forEach(s => spacesMap[s.id] = s);
      setSpaceTypes(spacesMap);
      
      const storesMap = {};
      storesRes.data.forEach(s => storesMap[s.id] = s);
      setStores(storesMap);
      
      // Load modifications history
      try {
        const modsRes = await modificationsAPI.getAll(id);
        setModifications(modsRes.data || []);
      } catch (e) {
        console.log('No modifications found');
      }
      
      // If request is modified, load the pending modification preview
      if (requestRes.data.status === 'MODIFIED') {
        try {
          const previewRes = await modificationsAPI.getPreview(id);
          setPendingModification(previewRes.data);
        } catch (e) {
          console.log('No pending modification');
        }
      }
    } catch (error) {
      toast.error('Error al cargar solicitud');
    } finally {
      setLoading(false);
    }
  };

  const getFileUrl = (url) => {
    if (!url) return '';
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    if (url.startsWith('/static/artworks/')) {
      const filename = url.replace('/static/artworks/', '');
      return `${backendUrl}/api/files/artworks/${encodeURIComponent(filename)}`;
    }
    return `${backendUrl}${url}`;
  };

  const isImageFile = (url) => {
    if (!url) return false;
    const ext = url.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      await requestsAPI.addComment(id, { message: newComment });
      setNewComment('');
      toast.success('Comentario agregado');
      loadRequestDetail();
    } catch (error) {
      toast.error('Error al agregar comentario');
    }
  };

  const handleApproveReject = async (action) => {
    try {
      await requestsAPI.approveReject(id, {
        action,
        comments: actionComment
      });
      toast.success(action === 'approve' ? 'Solicitud aprobada' : 'Solicitud rechazada');
      setActionComment('');
      setShowApproveReject(false);
      loadRequestDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar solicitud');
    }
  };

  // Export request as Excel report
  const handleExportReport = async () => {
    setExporting(true);
    try {
      const response = await requestsAPI.exportReport(id);
      
      // Create blob and download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `reporte_solicitud_${request?.requested_month || 'export'}.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Reporte exportado exitosamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al exportar reporte');
    } finally {
      setExporting(false);
    }
  };

  // Open modify modal and prepare allocation changes
  const handleOpenModifyModal = () => {
    // Initialize allocation changes from current allocations
    const changes = [];
    request.line_items?.forEach(item => {
      item.allocations?.forEach(alloc => {
        changes.push({
          allocation_id: alloc.id,
          original_store_id: alloc.store_id,
          original_start_month: alloc.start_month || alloc.month,
          original_end_month: alloc.end_month || alloc.month,
          new_store_id: null,
          new_start_month: null,
          new_end_month: null,
          is_removed: false,
          is_new: false,
          line_item_id: alloc.line_item_id
        });
      });
    });
    setAllocationChanges(changes);
    setModificationNotes('');
    setShowModifyModal(true);
  };

  // Update an allocation change
  const updateAllocationChange = (index, field, value) => {
    const updated = [...allocationChanges];
    updated[index] = { ...updated[index], [field]: value };
    setAllocationChanges(updated);
  };

  // Submit modification
  const handleSubmitModification = async () => {
    // Filter only changes that have actual modifications
    const changesWithModifications = allocationChanges.filter(change => 
      change.is_removed || 
      change.new_store_id || 
      change.new_start_month || 
      change.new_end_month
    );
    
    if (changesWithModifications.length === 0) {
      toast.error('No hay cambios para enviar');
      return;
    }
    
    try {
      await modificationsAPI.create(id, {
        allocation_changes: changesWithModifications,
        admin_notes: modificationNotes
      });
      toast.success('Modificación enviada al proveedor');
      setShowModifyModal(false);
      loadRequestDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear modificación');
    }
  };

  // Provider reviews modification
  const handleReviewModification = async (action) => {
    if (!pendingModification?.modification) return;
    
    try {
      await modificationsAPI.review(id, pendingModification.modification.id, {
        action,
        notes: reviewNotes
      });
      toast.success(action === 'accept' ? 'Modificación aceptada' : 'Modificación rechazada');
      setShowReviewModal(false);
      setReviewNotes('');
      loadRequestDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar revisión');
    }
  };

  // Generate months for select
  const generateMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const value = date.toISOString().slice(0, 7);
      const label = date.toLocaleDateString('es-EC', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  };

  const monthOptions = generateMonthOptions();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!request) return null;

  const canApproveReject = (isExecutive || isAdmin) && ['SUBMITTED', 'REVIEW'].includes(request.status);

  return (
    <div data-testid="request-detail-page">
      <Button variant="ghost" onClick={() => navigate('/requests')} className="mb-6">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Volver a Solicitudes
      </Button>

      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">
            Solicitud - {request.requested_month}
          </h1>
          <p className="text-lg text-slate-600">
            {request.provider_company_name && <span className="font-medium">{request.provider_company_name} • </span>}
            Creada el {new Date(request.created_at).toLocaleDateString('es-EC')}
          </p>
        </div>
        <Badge className={statusColors[request.status]} data-testid="request-status">
          {statusLabels[request.status]}
        </Badge>
      </div>

      {/* Approve/Reject Section */}
      {canApproveReject && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="text-amber-900">Aprobar o Rechazar Solicitud</CardTitle>
            <CardDescription>Esta solicitud requiere tu revisión</CardDescription>
          </CardHeader>
          <CardContent>
            {!showApproveReject ? (
              <div className="flex gap-3">
                <Button 
                  onClick={() => setShowApproveReject(true)} 
                  variant="default"
                  data-testid="show-approve-reject-button"
                >
                  Revisar Solicitud
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label>Comentarios (opcional)</Label>
                  <Textarea
                    value={actionComment}
                    onChange={(e) => setActionComment(e.target.value)}
                    placeholder="Agrega comentarios sobre tu decisión..."
                    rows={3}
                    className="mt-1"
                  />
                </div>
                <div className="flex gap-3">
                  <Button 
                    onClick={() => handleApproveReject('approve')}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="approve-button"
                  >
                    <ThumbsUp className="w-4 h-4 mr-2" />
                    Aprobar
                  </Button>
                  <Button 
                    onClick={() => handleApproveReject('reject')}
                    variant="destructive"
                    data-testid="reject-button"
                  >
                    <ThumbsDown className="w-4 h-4 mr-2" />
                    Rechazar
                  </Button>
                  <Button 
                    onClick={() => {
                      setShowApproveReject(false);
                      setActionComment('');
                    }}
                    variant="outline"
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {request.status === 'REJECTED' && request.rejection_reason && (
        <Card className="mb-6 border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-900">Solicitud Rechazada</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-red-800">{request.rejection_reason}</p>
          </CardContent>
        </Card>
      )}

      {/* Admin: Modify Approved Request */}
      {isAdmin && ['APPROVED', 'MODIFICATION_ACCEPTED'].includes(request.status) && (
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="text-blue-900 flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Modificar Solicitud Aprobada
            </CardTitle>
            <CardDescription>Puede modificar tiendas y períodos de esta solicitud aprobada</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Button 
                onClick={handleOpenModifyModal}
                data-testid="modify-request-button"
              >
                <Edit className="w-4 h-4 mr-2" />
                Modificar Solicitud
              </Button>
              {modifications.length > 0 && (
                <Button 
                  variant="outline"
                  onClick={() => setShowHistoryModal(true)}
                >
                  <History className="w-4 h-4 mr-2" />
                  Ver Historial ({modifications.length})
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Export Report - Available for approved requests */}
      {['APPROVED', 'MODIFICATION_ACCEPTED', 'CONFIRMED'].includes(request.status) && (
        <Card className="mb-6 border-emerald-200 bg-emerald-50">
          <CardHeader>
            <CardTitle className="text-emerald-900 flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Exportar Reporte
            </CardTitle>
            <CardDescription>
              Genera un archivo Excel con la información consolidada de esta solicitud para TuVoz
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Button 
                onClick={handleExportReport}
                disabled={exporting}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="export-report-button"
              >
                {exporting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Generando...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Exportar Reporte Excel
                  </>
                )}
              </Button>
            </div>
            <p className="text-xs text-emerald-700 mt-3">
              Incluye: laboratorio/proveedor, puntos de venta, vigencias, elementos contratados, artes y observaciones
            </p>
          </CardContent>
        </Card>
      )}

      {/* Provider: Review Pending Modification */}
      {(isProvider || isAdmin) && request.status === 'MODIFIED' && pendingModification && (
        <Card className="mb-6 border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="text-orange-900 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Modificación Pendiente de Revisión
            </CardTitle>
            <CardDescription>
              El administrador ha propuesto cambios. Revise y acepte o rechace la modificación.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4 p-3 bg-white rounded-lg border">
              <p className="text-sm text-slate-600">
                <strong>Modificado por:</strong> {pendingModification.modification?.modified_by_name}
              </p>
              <p className="text-sm text-slate-600">
                <strong>Fecha:</strong> {new Date(pendingModification.modification?.created_at).toLocaleDateString('es-EC')}
              </p>
              <p className="text-sm text-slate-600">
                <strong>Cambios:</strong> {pendingModification.modification?.allocation_changes?.length || 0} asignación(es)
              </p>
              {pendingModification.modification?.admin_notes && (
                <p className="text-sm text-slate-600 mt-2">
                  <strong>Notas:</strong> {pendingModification.modification.admin_notes}
                </p>
              )}
            </div>
            <div className="flex gap-3">
              <Button 
                onClick={() => setShowReviewModal(true)}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="review-modification-button"
              >
                <Eye className="w-4 h-4 mr-2" />
                Revisar Cambios
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Line Items */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Productos Solicitados</CardTitle>
          <CardDescription>Detalle de espacios publicitarios</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {request.line_items?.map((item) => (
              <div key={item.id} className="flex items-center gap-4 p-4 border rounded-lg">
                <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                  <Package className="w-6 h-6 text-slate-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900">
                    {spaceTypes[item.space_type_id]?.name || 'Desconocido'}
                  </h3>
                  <p className="text-sm text-slate-600">
                    Cantidad: {item.total_quantity} | Producto: {item.product_name || 'N/A'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-slate-900">
                    ${spaceTypes[item.space_type_id]?.base_price_usd || 0}
                  </p>
                  <p className="text-xs text-slate-500">por unidad</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Allocations */}
      {request.line_items?.[0]?.allocations?.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Distribución por Tiendas</CardTitle>
            <CardDescription>Asignación de espacios por ubicación</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {request.line_items.map((item) =>
                item.allocations?.map((alloc) => (
                  <div key={alloc.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-slate-500" />
                      <div>
                        <p className="font-medium text-slate-900">
                          {spaceTypes[item.space_type_id]?.name || 'Producto'}
                          {stores[alloc.store_id]?.name && (
                            <span className="text-slate-500 font-normal"> — {stores[alloc.store_id]?.name}</span>
                          )}
                        </p>
                        <p className="text-sm text-slate-500">
                          {stores[alloc.store_id]?.city}, {stores[alloc.store_id]?.region}
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline">Cantidad: {alloc.quantity}</Badge>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Artworks Section */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Image className="w-5 h-5" />
            Artes Publicitarias
          </CardTitle>
          <CardDescription>Archivos de diseño subidos para esta solicitud</CardDescription>
        </CardHeader>
        <CardContent>
          {artworks.length === 0 ? (
            <div className="text-center py-8 bg-slate-50 rounded-lg">
              <Upload className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 mb-2">No hay artes subidas para esta solicitud</p>
              <p className="text-sm text-slate-400">El proveedor aún no ha cargado los archivos de diseño</p>
            </div>
          ) : (
            <div className="space-y-4">
              {artworks.map((artwork) => (
                <div key={artwork.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-slate-900">
                        {spaceTypes[artwork.space_type_id]?.name || 'Producto'}
                      </h4>
                      <p className="text-sm text-slate-500">
                        Subido el {new Date(artwork.created_at).toLocaleDateString('es-EC')}
                      </p>
                    </div>
                    <Badge className={
                      artwork.status === 'APPROVED' ? 'bg-emerald-100 text-emerald-700' :
                      artwork.status === 'REJECTED' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }>
                      {artwork.status === 'APPROVED' ? 'Aprobado' :
                       artwork.status === 'REJECTED' ? 'Rechazado' : 'Pendiente'}
                    </Badge>
                  </div>
                  
                  {/* File previews */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {(artwork.files || artwork.file_urls || []).map((url, index) => (
                      <div key={index} className="relative group">
                        {isImageFile(url) ? (
                          <img 
                            src={getFileUrl(url)} 
                            alt={`Arte ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg border"
                          />
                        ) : (
                          <div className="w-full h-32 bg-slate-100 rounded-lg border flex items-center justify-center">
                            <div className="text-center">
                              <Image className="w-8 h-8 text-slate-400 mx-auto mb-1" />
                              <span className="text-xs text-slate-500">PDF</span>
                            </div>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                          <Button size="sm" variant="secondary" asChild>
                            <a href={getFileUrl(url)} target="_blank" rel="noopener noreferrer">
                              <Eye className="w-4 h-4" />
                            </a>
                          </Button>
                          <Button size="sm" variant="secondary" asChild>
                            <a href={getFileUrl(url)} download>
                              <Download className="w-4 h-4" />
                            </a>
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Admin review buttons */}
                  {(isAdmin || isExecutive) && artwork.status === 'PENDING' && (
                    <div className="flex gap-2 mt-4 pt-4 border-t">
                      <Button 
                        size="sm" 
                        className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={async () => {
                          try {
                            await artworksAPI.review(artwork.id, { status: 'APPROVED' });
                            toast.success('Arte aprobado');
                            loadRequestDetail();
                          } catch (e) {
                            toast.error('Error al aprobar');
                          }
                        }}
                      >
                        <ThumbsUp className="w-4 h-4 mr-1" />
                        Aprobar Arte
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={async () => {
                          try {
                            await artworksAPI.review(artwork.id, { status: 'REJECTED', feedback: 'Requiere correcciones' });
                            toast.success('Arte rechazado');
                            loadRequestDetail();
                          } catch (e) {
                            toast.error('Error al rechazar');
                          }
                        }}
                      >
                        <ThumbsDown className="w-4 h-4 mr-1" />
                        Rechazar
                      </Button>
                    </div>
                  )}

                  {artwork.feedback && (
                    <div className="mt-3 p-3 bg-amber-50 rounded-lg">
                      <p className="text-sm text-amber-800"><strong>Feedback:</strong> {artwork.feedback}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notifications Section */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Notificaciones
          </CardTitle>
          <CardDescription>Conversación entre proveedor y farmacia</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {request.comments && request.comments.length > 0 ? (
              request.comments.map((comment) => (
                <div key={comment.id} className="flex gap-3 p-4 bg-slate-50 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-semibold text-primary">
                      {comment.user_name.charAt(0)}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-slate-900">{comment.user_name}</span>
                      <Badge variant="outline" className="text-xs">
                        {comment.user_role === 'ADMIN' ? 'Admin' : comment.user_role === 'EXECUTIVE' ? 'Ejecutivo' : 'Proveedor'}
                      </Badge>
                      <span className="text-xs text-slate-500">
                        {new Date(comment.created_at).toLocaleString('es-EC')}
                      </span>
                    </div>
                    <p className="text-slate-700">{comment.message}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-slate-500 py-4">No hay comentarios aún</p>
            )}
            
            <Separator />
            
            <div className="space-y-3">
              <Label>Agregar Comentario</Label>
              <Textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Escribe tu comentario aquí..."
                rows={3}
                data-testid="comment-input"
              />
              <Button 
                onClick={handleAddComment} 
                disabled={!newComment.trim()}
                data-testid="add-comment-button"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Agregar Comentario
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {request.notes && (
        <Card>
          <CardHeader>
            <CardTitle>Notas</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-700">{request.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Modification History */}
      {modifications.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Historial de Modificaciones
            </CardTitle>
            <CardDescription>Versiones anteriores de esta solicitud</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {modifications.map((mod) => (
                <div key={mod.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Versión {mod.version}</p>
                    <p className="text-sm text-slate-600">
                      Por {mod.modified_by_name} • {new Date(mod.created_at).toLocaleDateString('es-EC')}
                    </p>
                    <p className="text-sm text-slate-500">
                      {mod.allocation_changes?.length || 0} cambio(s)
                    </p>
                  </div>
                  <Badge className={
                    mod.status === 'ACCEPTED' ? 'bg-emerald-100 text-emerald-700' :
                    mod.status === 'REJECTED' ? 'bg-red-100 text-red-700' :
                    'bg-yellow-100 text-yellow-700'
                  }>
                    {mod.status === 'ACCEPTED' ? 'Aceptada' :
                     mod.status === 'REJECTED' ? 'Rechazada' : 
                     'Pendiente'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin Modify Modal */}
      <Dialog open={showModifyModal} onOpenChange={setShowModifyModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Modificar Solicitud Aprobada
            </DialogTitle>
            <DialogDescription>
              Modifique las tiendas o períodos de las asignaciones. El proveedor deberá aprobar los cambios.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-amber-800 text-sm">
                <strong>Nota:</strong> Solo puede modificar tiendas y períodos, no cantidades. 
                Los cambios serán enviados al proveedor para su aprobación.
              </p>
            </div>
            
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900">Asignaciones Actuales</h3>
              
              {allocationChanges.map((change, index) => {
                const lineItem = request.line_items?.find(li => li.id === change.line_item_id);
                return (
                  <div 
                    key={change.allocation_id} 
                    className={`border rounded-lg p-4 ${change.is_removed ? 'bg-red-50 border-red-200' : 'bg-white'}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-medium text-slate-900">
                          {spaceTypes[lineItem?.space_type_id]?.name || 'Producto'}
                        </p>
                        <p className="text-sm text-slate-600">
                          Original: {stores[change.original_store_id]?.name || 'Tienda'} • 
                          {change.original_start_month} a {change.original_end_month}
                        </p>
                      </div>
                      <Button
                        variant={change.is_removed ? "default" : "destructive"}
                        size="sm"
                        onClick={() => updateAllocationChange(index, 'is_removed', !change.is_removed)}
                      >
                        {change.is_removed ? 'Restaurar' : 'Eliminar'}
                      </Button>
                    </div>
                    
                    {!change.is_removed && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-3 border-t">
                        <div>
                          <Label className="text-xs">Nueva Tienda (opcional)</Label>
                          <Select
                            value={change.new_store_id || 'no_change'}
                            onValueChange={(val) => updateAllocationChange(index, 'new_store_id', val === 'no_change' ? null : val)}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Sin cambio" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="no_change">Sin cambio</SelectItem>
                              {storesList.map(store => (
                                <SelectItem key={store.id} value={store.id}>
                                  {store.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Nuevo Mes Inicio (opcional)</Label>
                          <Select
                            value={change.new_start_month || 'no_change'}
                            onValueChange={(val) => updateAllocationChange(index, 'new_start_month', val === 'no_change' ? null : val)}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Sin cambio" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="no_change">Sin cambio</SelectItem>
                              {monthOptions.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Nuevo Mes Fin (opcional)</Label>
                          <Select
                            value={change.new_end_month || 'no_change'}
                            onValueChange={(val) => updateAllocationChange(index, 'new_end_month', val === 'no_change' ? null : val)}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Sin cambio" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="no_change">Sin cambio</SelectItem>
                              {monthOptions.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            <div>
              <Label>Notas de Modificación</Label>
              <Textarea
                value={modificationNotes}
                onChange={(e) => setModificationNotes(e.target.value)}
                placeholder="Explique los motivos de la modificación..."
                rows={3}
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModifyModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmitModification} data-testid="submit-modification-btn">
              <Edit className="w-4 h-4 mr-2" />
              Enviar Modificación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Provider Review Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              Revisión de Modificación
            </DialogTitle>
            <DialogDescription>
              El administrador ha solicitado cambios en su solicitud aprobada. Por favor revise y acepte o rechace.
            </DialogDescription>
          </DialogHeader>
          
          {pendingModification && (
            <div className="space-y-6 py-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="font-medium text-orange-900">
                  Modificado por: {pendingModification.modification?.modified_by_name}
                </p>
                <p className="text-sm text-orange-800">
                  {new Date(pendingModification.modification?.created_at).toLocaleDateString('es-EC')}
                </p>
                {pendingModification.modification?.admin_notes && (
                  <p className="mt-2 text-orange-800">
                    <strong>Notas:</strong> {pendingModification.modification.admin_notes}
                  </p>
                )}
              </div>
              
              <div>
                <h3 className="font-semibold text-slate-900 mb-3">Cambios Propuestos</h3>
                <div className="space-y-3">
                  {pendingModification.modification?.allocation_changes?.map((change, index) => (
                    <div key={index} className="border rounded-lg p-4">
                      {change.is_removed ? (
                        <div className="flex items-center gap-2 text-red-600">
                          <XCircle className="w-5 h-5" />
                          <span className="font-medium">Asignación eliminada</span>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="text-slate-600">Tienda:</span>
                            <span>{change.original_store_name || stores[change.original_store_id]?.name}</span>
                            {change.new_store_id && (
                              <>
                                <ArrowRight className="w-4 h-4 text-orange-500" />
                                <span className="font-medium text-orange-700">
                                  {change.new_store_name || stores[change.new_store_id]?.name}
                                </span>
                              </>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-slate-600">Período:</span>
                            <span>{change.original_start_month} - {change.original_end_month}</span>
                            {(change.new_start_month || change.new_end_month) && (
                              <>
                                <ArrowRight className="w-4 h-4 text-orange-500" />
                                <span className="font-medium text-orange-700">
                                  {change.new_start_month || change.original_start_month} - {change.new_end_month || change.original_end_month}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <Label>Notas de Respuesta (opcional)</Label>
                <Textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Agregue comentarios sobre su decisión..."
                  rows={3}
                  className="mt-1"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReviewModal(false)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleReviewModification('reject')}
              data-testid="reject-modification-btn"
            >
              <ThumbsDown className="w-4 h-4 mr-2" />
              Rechazar Cambios
            </Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => handleReviewModification('accept')}
              data-testid="accept-modification-btn"
            >
              <ThumbsUp className="w-4 h-4 mr-2" />
              Aceptar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
